import { Button } from "components/DesignSystem/Button/Button";
import { FormikForm } from "components/FormikForm/FormikForm";
import Modal from "components/DesignSystem/Modal/Modal";
import { Stepper } from "components/DesignSystem/Stepper/Stepper";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { BackendError } from "types/utils/error";
import Loader from "components/DesignSystem/Loader/loader";
import { reviewValidationSchema } from "formValidations/BulkJournalEntrySchema";
import {
  usePopulateBulkJournalEntryMutation,
  useLazyGetBulkJournalEntryReviewQuery,
  UnbalanceJE,
} from "store/apis/journalEntry";
import {
  ImportJournalEntry,
  UploadedFileCard,
} from "./BulkJournalEntryStep/ImportJournalEntry";
import { ReviewStep } from "./BulkJournalEntryStep/ReviewEntries";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Check } from "components/icons/Check";
import { ArrowRight } from "components/icons/ArrowRight";
import BankWarningCircle from "static/images/BankWarningCircle.svg";

type BulkJournalEntryUploadProps = { isOpen: boolean; close: () => void };

const STEP_IMPORT = 1;
const STEP_REVIEW = 2;

const breadcrumbs = [
  { name: "Import entries", step: STEP_IMPORT },
  { name: "Review entries", step: STEP_REVIEW },
];

const computeTotalUnbalanced = (groups: UnbalanceJE[]) =>
  parseFloat(
    groups
      .reduce((sum, group) => {
        const allEntries = [
          ...group.journal_items,
          ...(group.manual_entries || []),
        ];
        return sum + allEntries.reduce((s, e) => s + (e.amount || 0), 0);
      }, 0)
      .toFixed(2)
  );

export const BulkJournalEntryUpload = ({
  isOpen,
  close,
}: BulkJournalEntryUploadProps) => {
  const { alertToast, info: infoToast } = useToast();
  const entityId = useCurrentEntityId();

  const [step, setStep] = useState(STEP_IMPORT);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [bulkFileId, setBulkFileId] = useState<string | null>(null);

  const [populateBulkJournalEntry, { isLoading: isPopulating }] =
    usePopulateBulkJournalEntryMutation();

  const [
    fetchReview,
    {
      data: journalEntriesData,
      isLoading: isReviewLoading,
      isSuccess: isReviewSuccess,
      isError,
    },
  ] = useLazyGetBulkJournalEntryReviewQuery();

  const handleUpload = async (journalEntries: UnbalanceJE[]) => {
    if (!bulkFileId) return;
    try {
      await populateBulkJournalEntry({
        bulkFileId,
        entityId,
        journalEntries,
      }).unwrap();

      infoToast({
        title: "Adding Journal Entries",
        message: "We will send a notification once it's complete.",
        duration: 5000,
      });
      close();
    } catch (error) {
      alertToast({
        title: "Journal entries could not be added",
        message:
          (error as BackendError).data?.error?.message ||
          "We found some error in the uploaded file. Please check and retry.",
      });
    }
  };

  const handleClose = () => {
    setStep(STEP_IMPORT);
    setSelectedFile(null);
    setBulkFileId(null);
    close();
  };

  const handleStepperClick = (clickedStep: number) => {
    if (clickedStep < step) {
      setStep(clickedStep);
    }
  };

  const handleNext = async () => {
    if (!bulkFileId) return;
    try {
      setStep(STEP_REVIEW);
      await fetchReview({ entityId, bulkFileId }).unwrap();
    } catch (error) {}
  };

  const handleDeleteOnReview = () => {
    setStep(STEP_IMPORT);
    setSelectedFile(null);
    setBulkFileId(null);
  };

  const journalEntries = journalEntriesData || [];

  return (
    <Modal.Root open={isOpen} onOpenChange={handleClose}>
      <Modal.Content
        size={
          step === STEP_REVIEW && isReviewSuccess && journalEntries.length > 0
            ? "xxxl"
            : "large"
        }
        useCustomOverlay
      >
        <Modal.Header>
          <div className="t-flex t-flex-col t-gap-1">
            <Modal.Title>Add Journal Entries in Bulk</Modal.Title>
            <Stepper size="small" direction="horizontal">
              {breadcrumbs.map(({ name, step: s }, i) => (
                <Stepper.Step
                  key={s}
                  step={s}
                  isActive={step >= s}
                  clickable={step > s}
                  onClick={() => handleStepperClick(s)}
                >
                  <div className="t-flex t-items-center">
                    {name}
                    {i < breadcrumbs.length - 1 && (
                      <ArrowRight color="currentColor" />
                    )}
                  </div>
                </Stepper.Step>
              ))}
            </Stepper>
          </div>
          <Modal.Close />
        </Modal.Header>

        {step === STEP_IMPORT && (
          <>
            <ImportJournalEntry
              setBulkFileId={setBulkFileId}
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
            />
            <Modal.FooterButtonGroup>
              <Button type="button" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                customType="primary"
                onClick={handleNext}
                disabled={!bulkFileId}
                isLoading={isReviewLoading}
              >
                Next
              </Button>
            </Modal.FooterButtonGroup>
          </>
        )}

        {step === STEP_REVIEW && (
          <FormikForm
            initialValues={{ journalEntries: journalEntries }}
            enableReinitialize
            validationSchema={reviewValidationSchema}
            validateOnBlur={false}
            onSubmit={(values) => handleUpload(values.journalEntries)}
          >
            {({ values }) => {
              const isAllBalanced =
                computeTotalUnbalanced(values.journalEntries) === 0;

              return (
                <>
                  <Modal.Body className="t-overflow-y-auto t-max-h-[60vh]">
                    <Async.Root
                      isLoading={isReviewLoading}
                      isEmpty={journalEntries.length === 0}
                      isSuccess={isReviewSuccess}
                      isError={isError}
                      customLoader={
                        <div className="t-flex t-flex-col t-items-center t-justify-center t-gap-3.5 t-py-16">
                          <Loader size="small" />
                          <div className="t-text-body-sm t-text-text-60 t-font-medium">
                            Checking your file...
                          </div>
                          <div className="t-text-body-sm t-text-text-30 t-text-center">
                            We're validating the entries and flagging anything
                            that needs attention.
                          </div>
                        </div>
                      }
                    >
                      <Async.ErrorHandler>
                        <div className="t-flex t-flex-col t-items-center t-justify-center t-gap-5">
                          <div className="t-flex t-flex-col t-items-center t-justify-center t-gap-3">
                            <img
                              src={BankWarningCircle}
                              alt="BankWarningCircle"
                              className="t-size-9"
                            />
                            <div className="t-text-center t-text-body-sm t-text-text-60">
                              Issues found in the uploaded file
                              <br />
                              Update the file accordingly and re-upload it
                            </div>
                          </div>
                          {selectedFile && (
                            <div className="t-w-full">
                              <UploadedFileCard
                                selectedFile={selectedFile}
                                onDelete={handleDeleteOnReview}
                              />
                            </div>
                          )}
                        </div>
                      </Async.ErrorHandler>
                      <Async.Empty>
                        <div className="t-flex t-flex-col t-items-center t-justify-center t-gap-5">
                          <div className="t-flex t-flex-col t-items-center t-justify-center t-gap-3">
                            <Check className="t-size-9 t-text-dark_green" />
                            <div className="t-text-center t-text-body-sm t-text-text-60">
                              All journal entries are balanced and ready to
                              upload.
                              <br />
                              Click "Upload" to add them to your account.
                            </div>
                          </div>
                          {selectedFile && (
                            <div className="t-w-full">
                              <UploadedFileCard
                                selectedFile={selectedFile}
                                onDelete={handleDeleteOnReview}
                              />
                            </div>
                          )}
                        </div>
                      </Async.Empty>
                      <Async.Success>
                        <ReviewStep />
                      </Async.Success>
                    </Async.Root>
                  </Modal.Body>

                  <Modal.FooterButtonGroup>
                    <Button type="button" onClick={handleClose}>
                      Cancel
                    </Button>
                    <Button
                      customType="primary"
                      type="submit"
                      isLoading={isPopulating}
                      disabled={isPopulating || !isAllBalanced || isError}
                    >
                      Upload
                    </Button>
                  </Modal.FooterButtonGroup>
                </>
              );
            }}
          </FormikForm>
        )}
      </Modal.Content>
    </Modal.Root>
  );
};
